// src/lib/kinema/konva/Compositor.tsx
import { useRef, useEffect } from 'react';
import Konva from 'konva';
import { Reconciler } from './reconciler';
import { useEditorContext } from './EditorProvider';
import { getCompositionProps } from '../core/types';
import { UPDATE_PROPERTY } from '../core/commands/commands';
import { useEditorState } from './hooks';
import { builtInRenderers } from './renderers';
import type { RendererEntry } from './renderers/types';
import { GestureOverrideManager } from '../core/tools/GestureOverride';
import { ToolManager } from '../core/tools/ToolManager';
import { SelectTool } from '../core/tools/SelectTool';
import { PanTool } from '../core/tools/PanTool';
import { KonvaHitTestProvider } from './interaction/KonvaHitTestProvider';
import { KonvaViewportProvider } from './interaction/KonvaViewportProvider';
import { KonvaEventAdapter } from './interaction/KonvaEventAdapter';
import { SelectionOverlay } from './overlays/SelectionOverlay';
import { MarqueeOverlay } from './overlays/MarqueeOverlay';
import { SnapGuides } from './overlays/SnapGuides';
import { PenOverlay } from './overlays/PenOverlay';
import { PathEditOverlay } from './overlays/PathEditOverlay';
import { PenTool } from '../core/tools/PenTool';

interface CompositorProps {
  className?: string;
  renderers?: RendererEntry[];
  onToolManagerReady?: (toolManager: ToolManager) => void;
  onMainLayerReady?: (layer: Konva.Layer) => void;
}

export function Compositor({ className, renderers, onToolManagerReady, onMainLayerReady }: CompositorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const reconcilerRef = useRef<Reconciler | null>(null);
  const adapterRef = useRef<KonvaEventAdapter | null>(null);
  const selectionOverlayRef = useRef<SelectionOverlay | null>(null);
  const marqueeOverlayRef = useRef<MarqueeOverlay | null>(null);
  const toolManagerRef = useRef<ToolManager | null>(null);
  const penOverlayRef = useRef<PenOverlay | null>(null);
  const penToolRef = useRef<PenTool | null>(null);
  const pathEditOverlayRef = useRef<PathEditOverlay | null>(null);
  const canvasBgRef = useRef<Konva.Rect | null>(null);
  const editor = useEditorContext();
  const state = useEditorState();

  // Initialize reconciler and interaction system
  useEffect(() => {
    if (!containerRef.current) return;
    const comp = getCompositionProps(editor.getState());
    const canvas = { width: comp.width, height: comp.height };

    // Stage fills the container — not limited to canvas size
    const containerWidth = containerRef.current.clientWidth || canvas.width;
    const containerHeight = containerRef.current.clientHeight || canvas.height;

    // Create gesture override manager and reconciler
    const gestureOverrides = new GestureOverrideManager();
    const reconciler = new Reconciler(containerRef.current, containerWidth, containerHeight, gestureOverrides);

    // Register built-in renderers + any additional ones
    reconciler.registerRenderers(builtInRenderers);
    if (renderers) {
      reconciler.registerRenderers(renderers);
    }
    reconcilerRef.current = reconciler;

    // Clip the content layer to the canvas bounds — layers outside are hidden
    const mainLayer = reconciler.getMainLayer();
    mainLayer.clip({ x: 0, y: 0, width: canvas.width, height: canvas.height });

    // Add a background rect to show the canvas bounds on the content layer
    const canvasBg = new Konva.Rect({
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height,
      fill: '#ffffff',
      stroke: '#334155',
      strokeWidth: 1,
      listening: false,
    });
    mainLayer.add(canvasBg);
    canvasBg.moveToBottom();
    canvasBgRef.current = canvasBg;

    // Fit canvas to viewport: scale and center so the canvas is fully visible
    const stage = reconciler.getStage();
    const padding = 40; // px padding around the canvas
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;
    const fitScale = Math.min(
      availableWidth / canvas.width,
      availableHeight / canvas.height,
      1, // don't zoom in beyond 100%
    );
    const offsetX = (containerWidth - canvas.width * fitScale) / 2;
    const offsetY = (containerHeight - canvas.height * fitScale) / 2;
    stage.scale({ x: fitScale, y: fitScale });
    stage.position({ x: offsetX, y: offsetY });

    // Create overlay layer (separate from content layer)
    const overlayLayer = new Konva.Layer();
    stage.add(overlayLayer);

    // Create overlays on the overlay layer
    const selectionOverlay = new SelectionOverlay(overlayLayer);
    selectionOverlay.setCallbacks({
      onAnchorDragEnd: (layerId, newAnchorX, newAnchorY) => {
        // Move anchor: update anchorX/anchorY AND x/y so the visual doesn't shift
        const layer = editor.getState().layers[layerId];
        if (!layer) return;
        const sx = layer.scaleX;
        const sy = layer.scaleY;
        const newX = layer.x + (newAnchorX - layer.anchorX) * sx;
        const newY = layer.y + (newAnchorY - layer.anchorY) * sy;
        editor.batch(() => {
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'anchorX', value: newAnchorX });
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'anchorY', value: newAnchorY });
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'x', value: newX });
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'y', value: newY });
        });
      },
    });
    const marqueeOverlay = new MarqueeOverlay(overlayLayer);
    const snapGuidesOverlay = new SnapGuides(overlayLayer);
    const penOverlayInst = new PenOverlay(overlayLayer);
    selectionOverlayRef.current = selectionOverlay;
    marqueeOverlayRef.current = marqueeOverlay;

    // Create Konva providers
    const hitTest = new KonvaHitTestProvider(stage, () => reconciler.getNodeMap());
    const viewport = new KonvaViewportProvider(stage);

    // Create ToolManager and register tools
    const toolManager = new ToolManager();
    const selectTool = new SelectTool(editor, hitTest, gestureOverrides);
    const panTool = new PanTool(viewport);
    const penTool = new PenTool(editor);
    penTool.onFinish = () => toolManager.setActiveTool('select');
    toolManager.register(selectTool);
    toolManager.register(panTool);
    toolManager.register(penTool);
    toolManagerRef.current = toolManager;

    penOverlayRef.current = penOverlayInst;
    penToolRef.current = penTool;

    // Path edit overlay
    const pathEditOverlayInst = new PathEditOverlay(overlayLayer);
    pathEditOverlayRef.current = pathEditOverlayInst;

    // Create event adapter and wire snap guides
    const adapter = new KonvaEventAdapter(stage, toolManager, editor, selectionOverlay.getTransformer());
    adapter.setSnapGuides(snapGuidesOverlay);
    adapter.setMarqueeOverlay(marqueeOverlay);
    adapter.setPenOverlay(penOverlayInst);
    adapter.setPathEditOverlay(pathEditOverlayInst);
    adapter.setSelectionOverlay(selectionOverlay);
    adapterRef.current = adapter;

    // When any tool activates, sync node listening/draggable state immediately
    for (const tool of [selectTool, panTool, penTool] as any[]) {
      const originalActivate = tool.activate?.bind(tool);
      tool.activate = () => {
        originalActivate?.();
        adapter.syncToolState();
      };
    }

    // Notify consumer if callback provided
    onToolManagerReady?.(toolManager);

    onMainLayerReady?.(reconciler.getMainLayer());

    return () => {
      reconciler.destroy();
      reconcilerRef.current = null;
      adapterRef.current = null;
      selectionOverlayRef.current = null;
      marqueeOverlayRef.current = null;
      toolManagerRef.current = null;
    };
  }, [editor]); // only on mount

  // During play, repaint the canvas every rAF so video frames appear at the
  // browser's native frame rate rather than being gated to editor state ticks
  // (which fire only at composition frame boundaries — typically 24fps).
  useEffect(() => {
    if (!state.isPlaying) return;
    let rafId = 0;
    const loop = () => {
      reconcilerRef.current?.getMainLayer().batchDraw();
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [state.isPlaying]);

  // Re-layout when composition dimensions change
  const comp = getCompositionProps(state);
  useEffect(() => {
    const reconciler = reconcilerRef.current;
    const canvasBg = canvasBgRef.current;
    if (!reconciler || !containerRef.current) return;

    const stage = reconciler.getStage();
    const mainLayer = reconciler.getMainLayer();

    // Update clip and background rect
    mainLayer.clip({ x: 0, y: 0, width: comp.width, height: comp.height });
    if (canvasBg) {
      canvasBg.width(comp.width);
      canvasBg.height(comp.height);
    }

    // Re-fit to viewport
    const containerWidth = containerRef.current.clientWidth || comp.width;
    const containerHeight = containerRef.current.clientHeight || comp.height;
    const padding = 40;
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;
    const fitScale = Math.min(availableWidth / comp.width, availableHeight / comp.height, 1);
    const offsetX = (containerWidth - comp.width * fitScale) / 2;
    const offsetY = (containerHeight - comp.height * fitScale) / 2;
    stage.scale({ x: fitScale, y: fitScale });
    stage.position({ x: offsetX, y: offsetY });
    stage.batchDraw();
  }, [comp.width, comp.height]);

  // Update reconciler when state changes
  useEffect(() => {
    const reconciler = reconcilerRef.current;
    const adapter = adapterRef.current;
    const selectionOverlay = selectionOverlayRef.current;
    const marqueeOverlay = marqueeOverlayRef.current;
    const toolManager = toolManagerRef.current;

    if (!reconciler) return;

    reconciler.update(
      state.layers,
      state.playheadTime,
      editor.getInterpolatorRegistry().getAll(),
      state.isPlaying,
    );

    // Sync adapter with current node map
    if (adapter) {
      adapter.syncEventListeners(reconciler.getNodeMap());
    }

    // Update selection overlay
    if (selectionOverlay) {
      selectionOverlay.updateSelection(state.selectedIds, reconciler.getNodeMap());
      // Disable anchor dragging when not in select tool
      const isSelectTool = toolManager?.getActiveTool().name === 'select';
      selectionOverlay.setAnchorDraggable(isSelectTool ?? false);
      // Keep anchor marker synced with node position
      selectionOverlay.refreshAnchorPosition();
    }

    // Update marquee overlay if SelectTool has an active marquee rect
    if (marqueeOverlay && toolManager) {
      const activeTool = toolManager.getActiveTool();
      if (activeTool.name === 'select') {
        const selectTool = activeTool as SelectTool;
        const marqueeRect = selectTool.getMarqueeRect();
        if (marqueeRect) {
          marqueeOverlay.showMarquee(marqueeRect);
        } else {
          marqueeOverlay.clear();
        }

        // Update path edit overlay
        const pathEditOverlay = pathEditOverlayRef.current;
        if (pathEditOverlay) {
          if (selectTool.isEditingPath()) {
            const layerId = selectTool.getEditingPathLayerId()!;
            const layer = state.layers[layerId];
            const points = selectTool.getEditingPathPoints();
            if (layer && points) {
              const offsetX = layer.x - layer.anchorX * layer.scaleX;
              const offsetY = layer.y - layer.anchorY * layer.scaleY;

              pathEditOverlay.setCallbacks({
                onPointDragMove: () => {},
                onPointDragEnd: (idx, x, y) => selectTool.handlePointDrag(idx, x, y),
                onHandleDragMove: () => {},
                onHandleDragEnd: (idx, type, dx, dy) => selectTool.handleHandleDrag(idx, type, dx, dy),
                onPointClick: (idx) => selectTool.handlePointClick(idx),
                onSegmentClick: (afterIdx) => selectTool.handleSegmentClick(afterIdx),
              });

              pathEditOverlay.show(points, selectTool.isEditingPathClosed(), offsetX, offsetY, layer.scaleX, layer.scaleY);
              pathEditOverlay.setSelectedPoint(selectTool.getSelectedPointIndex());

              // Hide the Transformer when editing path
              selectionOverlay?.updateSelection(new Set(), reconciler.getNodeMap());
            }
          } else {
            pathEditOverlay.hide();
          }
        }
      }
    }
  }, [state, editor]);

  return <div ref={containerRef} className={className} style={{ width: '100%', height: '100%' }} />;
}
